import apiClient from './client';

export interface SheetBinding {
  id: number;
  source_key: string;
  target_type: 'asset' | 'loan' | 'revenue_stream' | 'cash_flow';
  target_id: number;
  target_field: string;
  target_name: string | null;
  write_mode: 'measurement' | 'direct';
  is_active: boolean;
  guard_pct: string | null;
  guard_min: string | null;
  guard_max: string | null;
  expect_direction: 'any' | 'up' | 'down';
  allow_zero: boolean;
  last_value: string | null;
  last_synced_at: string | null;
  last_status: 'never' | 'ok' | 'held' | 'error' | 'stale' | 'missing';
  notes: string | null;
}

export interface SheetPendingChange {
  id: number;
  binding_id: number;
  source_key: string;
  target_type: string;
  target_id: number;
  target_field: string;
  target_name: string | null;
  proposed_value: string | null;
  previous_value: string | null;
  delta_pct: string | null;
  as_of: string | null;
  raw_value: string | null;
  reason:
    | 'guard_pct'
    | 'guard_bounds'
    | 'direction'
    | 'zero_value'
    | 'not_numeric'
    | 'error_cell'
    | 'stale_as_of';
  status: string;
  created_at: string | null;
}

export interface SheetSyncRun {
  id: number;
  source: string;
  status: string;
  generated_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  counts: Record<string, number>;
}

export interface SheetBindingCreate {
  source_key: string;
  target_type: SheetBinding['target_type'];
  target_id: number;
  target_field: string;
  guard_pct?: number | null;
  guard_min?: number | null;
  guard_max?: number | null;
  expect_direction?: SheetBinding['expect_direction'] | null;
  allow_zero?: boolean | null;
  notes?: string | null;
}

export const sheetSyncApi = {
  listBindings: () =>
    apiClient.get<SheetBinding[]>('/api/sheet-bindings/').then(r => r.data),

  attach: (data: SheetBindingCreate) =>
    apiClient.post<SheetBinding>('/api/sheet-bindings/', data).then(r => r.data),

  updateBinding: (id: number, data: Partial<SheetBindingCreate> & { is_active?: boolean }) =>
    apiClient.patch<SheetBinding>(`/api/sheet-bindings/${id}`, data).then(r => r.data),

  detach: (id: number) =>
    apiClient.delete<SheetBinding>(`/api/sheet-bindings/${id}`).then(r => r.data),

  listPending: () =>
    apiClient.get<SheetPendingChange[]>('/api/sheet-bindings/pending/').then(r => r.data),

  pendingCount: () =>
    apiClient.get<{ count: number }>('/api/sheet-bindings/pending/count').then(r => r.data.count),

  approve: (id: number, note?: string) =>
    apiClient
      .post<SheetPendingChange>(`/api/sheet-bindings/pending/${id}/approve`, { note })
      .then(r => r.data),

  reject: (id: number, note?: string) =>
    apiClient
      .post<SheetPendingChange>(`/api/sheet-bindings/pending/${id}/reject`, { note })
      .then(r => r.data),

  listRuns: () =>
    apiClient.get<SheetSyncRun[]>('/api/sheet-bindings/runs/').then(r => r.data),

  /** Keys the sheet last sent that nothing is bound to — the attach dialog's picker. */
  unmatchedKeys: () =>
    apiClient.get<string[]>('/api/sheet-sync/unmatched-keys').then(r => r.data),
};
